// preissue_cycle (RS, CORE-052): the four checks before every daily validation.
import { useState } from 'react'
import { PREISSUE_CHECKS } from '@/domain/catalogue'
import type { ActionPanelProps } from '@/features/contracts'
import { PanelFrame } from '../frame'
import { usePanelCommand } from '../hooks'
import { allTicked, cycleForAction, tickProblems } from '../logic'
import { CycleHeader, MissingCycle, SigningStatement, TextField, TickList } from '../parts'

export function PreissueCyclePanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const cycle = cycleForAction(bundle, action)
  const run = usePanelCommand('preissue_cycle', bundle, onDone)
  const [checks, setChecks] = useState<Record<string, boolean>>({})
  const [planRef, setPlanRef] = useState('')
  const [conflictRef, setConflictRef] = useState('')
  const [tried, setTried] = useState(false)

  const problems = [
    ...tickProblems(PREISSUE_CHECKS, checks, 'pre-issue check'),
    ...(planRef.trim() ? [] : ['Enter the frozen plan or MoC reference.']),
    ...(conflictRef.trim() ? [] : ['Enter the conflict review reference.']),
  ]

  function submit() {
    setTried(true)
    if (!cycle || problems.length > 0) return
    void run.submit({
      permit_id: bundle.permit.id,
      cycle_id: cycle.id,
      checks: allTicked(PREISSUE_CHECKS, checks),
      plan_reference: planRef.trim(),
      conflict_review_reference: conflictRef.trim(),
    })
  }

  return (
    <PanelFrame
      action={action}
      run={run}
      problems={problems}
      tried={tried}
      onCancel={onCancel}
      onSubmit={submit}
      submitLabel="Sign pre-issue"
      blockedNote={cycle ? null : 'Cycle not found.'}
    >
      {cycle ? <CycleHeader cycle={cycle} /> : <MissingCycle />}
      <TickList
        title="Check before this cycle's validation"
        ruleIds={['CORE-052']}
        options={PREISSUE_CHECKS}
        value={checks}
        onChange={setChecks}
      />
      <p className="-mt-2 mb-4 text-xs text-ink-soft">If you cannot confirm an item, do not sign. Resolve it with the PA first.</p>
      <TextField
        label="Frozen plan or MoC reference"
        required
        value={planRef}
        onChange={setPlanRef}
        placeholder="e.g. 14-day plan week 45, line 12"
      />
      <TextField
        label="Conflict and SIMOPS review reference"
        required
        value={conflictRef}
        onChange={setConflictRef}
        placeholder="e.g. GIS conflict check, morning meeting notes"
      />
      {cycle ? (
        <SigningStatement role="RS" person={bundle.viewer.person}>
          <p>
            I checked the four items above for cycle {cycle.cycle_number} today, against the references I gave. I pre-issue
            cycle {cycle.cycle_number} for RS validation.
          </p>
          <p>This pre-issue is for cycle {cycle.cycle_number} only. It does not carry over to any other cycle.</p>
        </SigningStatement>
      ) : null}
    </PanelFrame>
  )
}
